import EditFiles from '@/components/EditFiles';
import TieredCommissionInput from '@/components/TieredCommissionInput';
import UploadFilesButton from '@/components/uploadFileButton';
import prepareTranslate from '@/helpers/dictionary';
import { ACCORD_TYPE } from '@/helpers/enums';
import {
  Button,
  Center,
  Grid,
  MultiSelect,
  NumberInput,
  Select,
  TextInput,
  Textarea,
} from '@mantine/core';
import { MonthPickerInput } from '@mantine/dates';
import PropTypes from 'prop-types';

function CreateAgentAgreementInProcess(props) {
  const {
    form,
    productsOptions,
    companiesOptions,
    disabled,
  } = props;

  const t = prepareTranslate();

  const agreementIndex = form.getValues().agreements && form.getValues().agreements.length - 1;
  // eslint-disable-next-line max-len
  const agreementAccordType = form.getValues().agreements && form.getValues().agreements[agreementIndex].accordType;
  const currentAgreement = form.getValues().agreements?.[agreementIndex] || {};
  const hasDepositFees = (currentAgreement.agentFeeMonthlyDeposit > 0
    || currentAgreement.agentFeeMonthlyAccumulation > 0);
  const hasMeaningfulTier = (tiers) => Array.isArray(tiers)
    && tiers.some((tier) => tier.rate > 0 || tier.to !== null);
  const hasAccumulationFees = hasMeaningfulTier(currentAgreement.agentFeeAnnualDeposit)
    || hasMeaningfulTier(currentAgreement.agentFeeAnnualAccumulation);

  const onFileSelected = (files) => {
    const formattedFiles = files.map((file) => ({ file, name: file.name }));
    const existingFiles = form.values.agreements?.[agreementIndex]?.files || [];
    const updatedFiles = [...existingFiles, ...formattedFiles];
    form.setFieldValue(`agreements.${agreementIndex}.files`, updatedFiles);
  };

  const onFileNameChange = (index, newName) => {
    const existingFiles = form.values.agreements?.[agreementIndex]?.files || [];
    const updated = [...existingFiles];
    updated[index] = { ...updated[index], name: newName };
    form.setFieldValue(`agreements.${agreementIndex}.files`, updated);
  };

  const onDeleteFile = (index) => {
    form.removeListItem(`agreements.${agreementIndex}.files`, index);
  };

  return (
    <>
      <Grid gutter='lg'>
        <Grid.Col span={{ base: 6, md: 3 }}>
          <Select
            autoComplete='off'
            label={t('components.createAgentAgreementInProcess.company')}
            data={companiesOptions}
            withAsterisk
            maxLength={20}
            disabled= {disabled}
            {...form.getInputProps(`agreements.${agreementIndex}.companyId`)}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 6, md: 3 }}>
          <MultiSelect
            withAsterisk
            label={t('components.createAgentAgreementInProcess.branch')}
            data={productsOptions}
            {...form.getInputProps(`agreements.${agreementIndex}.branchId`)}
            disabled= {disabled}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 6, md: 3 }}>
          <TextInput
            autoComplete='off'
            withAsterisk
            label={t('components.createAgentAgreementInProcess.agent')}
            maxLength={10}
            {...form.getInputProps(`agreements.${agreementIndex}.agentNumber`)}
            disabled= {disabled}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 6, md: 3 }}>
          <Select
            withAsterisk
            label={t('components.createAgentAgreementInProcess.accordType')}
            data={ACCORD_TYPE}
            {...form.getInputProps(`agreements.${agreementIndex}.accordType`)}
            disabled= {disabled}
          />
        </Grid.Col>
      </Grid>
      <Grid gutter='lg'>
      <Grid.Col span={{ base: 6, md: 3 }}>
          <MonthPickerInput
            withAsterisk
            required={hasDepositFees}
            maxDate={new Date()}
            defaultValue={new Date()}
            label={t('components.createAgentAgreementInProcess.depositFrom')}
            {...form.getInputProps(`agreements.${agreementIndex}.depositFrom`)}
            disabled= {disabled}
            onChange={(value) => {
              form.setFieldValue(`agreements.${agreementIndex}.depositFrom`, value);
              if (value && currentAgreement.depositUntil && value > currentAgreement.depositUntil) {
                form.setFieldValue(`agreements.${agreementIndex}.depositUntil`, null);
              }
            }}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 6, md: 3 }}>
          <MonthPickerInput
            defaultValue={new Date()}
            disabled={disabled || !currentAgreement.depositFrom}
            minDate={currentAgreement.depositFrom ?? undefined}
            label={t('components.createAgentAgreementInProcess.depositUntil')}
            {...form.getInputProps(`agreements.${agreementIndex}.depositUntil`)}
          />
        </Grid.Col>
        {
          agreementAccordType === 'independent' ? (
            <>
              <Grid.Col span={{ base: 6, md: 3 }}>
                <NumberInput
                  autoComplete='off'
                  label={t('components.createAgentAgreementInProcess.commision.agentFeeMonthlyAccumulation')}
                  maxLength={5}
                  min={0}
                  {...form.getInputProps(`agreements.${agreementIndex}.agentFeeMonthlyAccumulation`)}
                  disabled={disabled}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 6, md: 3 }}>
                <NumberInput
                  autoComplete='off'
                  label={t('components.createAgentAgreementInProcess.commision.agentFeeMonthlyDeposit')}
                  maxLength={5}
                  min={0}
                  {...form.getInputProps(`agreements.${agreementIndex}.agentFeeMonthlyDeposit`)}
                  disabled={disabled}
                />
              </Grid.Col>
            </>
          ) : (
            <Grid.Col span={{ base: 6, md: 3 }}>
              <NumberInput
                autoComplete='off'
                label={t('components.createAgentAgreementInProcess.commision.employeeAgentCommission')}
                maxLength={5}
                min={0}
                {...form.getInputProps(`agreements.${agreementIndex}.employeeAgentCommission`)}
                disabled= {disabled}
              />
            </Grid.Col>

          )
        }
        <Grid.Col span={{ base: 6, md: 3 }}>
          <TextInput
            label={t('components.createAgentAgreementInProcess.accordName')}
            maxLength={200}
            min={0}
            {...form.getInputProps(`agreements.${agreementIndex}.accordName`)}
            disabled={disabled}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 6, md: 3 }}>
          <Textarea
            label={t('components.createAgentAgreementInProcess.comments')}
            minRows={3}
            {...form.getInputProps(`agreements.${agreementIndex}.comments`)}
            disabled={disabled}
          />
        </Grid.Col>
      </Grid>
      <Grid>
        <Grid.Col span={{ base: 6, md: 3 }}>
          <MonthPickerInput
            required={hasAccumulationFees}
            maxDate={new Date()}
            label={t('components.createAgentAgreementInProcess.accumulationFrom')}
            {...form.getInputProps(`agreements.${agreementIndex}.accumulationFrom`)}
            disabled={disabled}
            onChange={(value) => {
              form.setFieldValue(`agreements.${agreementIndex}.accumulationFrom`, value);
              // eslint-disable-next-line max-len
              if (value && currentAgreement.accumulationUntil && value > currentAgreement.accumulationUntil) {
                form.setFieldValue(`agreements.${agreementIndex}.accumulationUntil`, null);
              }
            }}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 6, md: 3 }}>
          <MonthPickerInput
            disabled={disabled || !currentAgreement.accumulationFrom}
            minDate={currentAgreement.accumulationFrom ?? undefined}
            label={t('components.createAgentAgreementInProcess.accumulationUntil')}
            {...form.getInputProps(`agreements.${agreementIndex}.accumulationUntil`)}
          />
        </Grid.Col>

        <Grid.Col span={12}>
          <TieredCommissionInput
            label={t('components.createAgentAgreementInProcess.commision.agentFeeAnnualDeposit')}
            {...form.getInputProps(`agreements.${agreementIndex}.agentFeeAnnualDeposit`)}
          />
        </Grid.Col>
        <Grid.Col span={12}>
          <TieredCommissionInput
            label={t('components.createAgentAgreementInProcess.commision.agentFeeAnnualAccumulation')}
            {...form.getInputProps(`agreements.${agreementIndex}.agentFeeAnnualAccumulation`)}
          />
        </Grid.Col>

      </Grid>
      <UploadFilesButton
        onFilesSelected={onFileSelected}
        label={t('pages.agencyCommissionsCreate.form.uploadFiles')}
      />

      {form.getValues()?.agreements?.[agreementIndex]?.files?.length > 0 && (
        <EditFiles
          files={form.getValues().agreements[agreementIndex].files}
          onNameChange={onFileNameChange}
          onRemove={onDeleteFile}
        />
      )}
      <Center mt='lg'>
        <Button
          type='submit'
        >
          {t('components.createAgentAgreementInProcess.submit')}
        </Button>
      </Center>
    </>
  );
}

CreateAgentAgreementInProcess.propTypes = {
  form: PropTypes.shape({
    setValues: PropTypes.func.isRequired,
    insertListItem: PropTypes.func.isRequired,
    getValues: PropTypes.func.isRequired,
    getInputProps: PropTypes.func.isRequired,
    setFieldValue: PropTypes.func.isRequired,
    removeListItem: PropTypes.func.isRequired,
    values: PropTypes.object.isRequired,
  }).isRequired,
  productsOptions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  })).isRequired,
  companiesOptions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  })).isRequired,
  currentIdentifier: PropTypes.shape({
    year: PropTypes.number.isRequired,
    month: PropTypes.number.isRequired,
    identifierNumber: PropTypes.string.isRequired,
    company: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
    product: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  createdAgent: PropTypes.shape({
    id: PropTypes.string.isRequired,
    string: PropTypes.string.isRequired,
  }),
  disabled: PropTypes.bool,
};

export default CreateAgentAgreementInProcess;
